import { useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { ActStrip, useConfirmAfterApplied } from './ActStrip.jsx'
import { ActionBar } from './ActionBar.jsx'
import AvatarCircle from './AvatarCircle.jsx'
import { FranchiseLinkControl } from './FranchiseLinkControl.jsx'
import { ManagePanel } from './ManagePanel.jsx'
import Person, { MemberLink, People } from './Person.jsx'
import {
  PosterSlot,
  ProviderLogo,
  StandingStrip,
  WhereThingsStandBlock,
} from './WatchPanel.jsx'
import { instantOf } from './freshness.js'
import {
  DISCORD_HOME,
  addedById,
  awaitingBaseText,
  comparePosition,
  countdownFor,
  defaultSeasonNumber,
  deriveYou,
  deriveYourProgress,
  easternClock,
  episodeCode,
  episodeCodeFromThreadKey,
  groupLeaderIds,
  hasReviewText,
  hueForTitle,
  imageUrl,
  isExactCountdown,
  isFinalEpisode,
  isPastInstant,
  isStale,
  isTonight,
  joinableId,
  kindLabel,
  memberProfilePath,
  membersWereDropped,
  membersWithStatus,
  monthYearLabel,
  providersOld,
  seriesTabsFor,
  shortEasternDate,
  sourceLabel,
  subscriptionsOf,
  toWatchInfo,
  weekdayEasternDate,
  whereWeAreCaption,
  yourStanding,
} from './watch.js'
import { reviewDisclaimer } from './reviewDisclaimer.js'
import {
  ARCHIVED,
  EPISODE_COLLAPSE_THRESHOLD,
  LOADING,
  MISCONFIGURED,
  NOT_FOUND,
  READY,
  collapsedEpisodeIndices,
  episodeDateLabel,
  episodeRevealKey,
  episodeRuns,
  episodeState,
  episodesOf,
  groupProgressWindowIndices,
  isGroupPosition,
  isYourPosition,
  progressCommand,
  seasonHasUnknownAirDate,
  seasonLabel,
  seasonsNotFetchedYet,
  seasonsOf,
  threadUrlFor,
  useNow,
  useWatchTitle,
} from './watchTitle.js'
import { yourArchiveEntry } from './watchArchive.js'
import { completedAtMovieNight, watchedWhenLabel } from './watchMember.js'
import {
  KIND_SET_PROGRESS,
  TARGET_STOPPED,
  TARGET_WATCHED,
  useWatchRequest,
} from './watchRequests.js'
import { SIGNED_IN, useSession } from '../session.js'
import { UNKNOWN_NAME, nameFor, useDirectory } from '../directory.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The title page — the route a Discord link (or the card's / archive row's
// "OPEN TITLE →") opens. W3b (P6-244), the last slice of the Watch tab.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SPECIFICATION IS `docs/design-briefs/watch/HANDOFF.md` §5, NOT THIS
//    FILE — `WatchPanel.jsx`'s own header, restated because it is still true
// ═══════════════════════════════════════════════════════════════════════════
//
// Every rendering decision below traces to §5's own prose or to a numbered
// finding in that document. Do not redesign anything it settles; a piece
// that could not be built as drawn is a finding in the report, not a silent
// substitution — see docs/reports/W3b.md.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NOTHING ON THIS PAGE WRITES (the brief's risk item 1, and ADR-0005 D2)
// ═══════════════════════════════════════════════════════════════════════════
//
// The one control that could look like a write — "Moving it on" — is
// copy-the-command: it never calls a Function that mutates anything, and its
// own honesty rules ARE the design (see `ProgressControl` below). Every
// other interactive element is a disclosure-free link: a season chip swaps
// which episodes are shown, a thread chip and "Open #watch ↗" both leave the
// page, and "Show title" reveals text that was already fetched — it asks
// nothing new of the network and writes nothing back to it.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 REUSE OVER RE-DRAWING — every piece this page shares with the card wears
//    the SAME component, exported from `WatchPanel.jsx`: `PosterSlot`,
//    `ProviderLogo`, `StandingStrip`, `WhereThingsStandBlock`. Two panels
//    deciding independently how a broken poster or a between-seasons show
//    reads is CLAUDE.md gotcha 1's *copies* axis; `kindLabel`/`sourceLabel`/
//    `DISCORD_HOME` moved into `watch.js` itself for the same reason.

/** Manager review round 2: the sentinel `WatchTitleBody`'s season-selection
 * state starts at — see that component's own header for why a sentinel
 * rather than a real season number. */
const SEASON_UNSET = Symbol('season-unset')

// ═══════════════════════════════════════════════════════════════════════════
// P6-303b — A FILM'S OWN MOVIE-NIGHT MARKING, DRAWN ON BOTH TITLE-PAGE PATHS
// ═══════════════════════════════════════════════════════════════════════════
//
// `utils/watch_projection.py::_movie_nights_view`'s own docstring: a film
// movie night's own reconcile creates is `listed: False` ("no crew view"),
// so it has no `_title_detail_view` object at all — `ArchivedTitleView`,
// below, takes no `detail` prop. `MovieNightsSection` is shared by BOTH
// paths for exactly that reason: the listed one reads `detail.movie_nights`,
// the archived one reads `archiveTitle.movie_nights` — the same shape,
// published by the same bot-side function, drawn by the same component here
// (CLAUDE.md gotcha 1's *copies* axis, one seam over).

/** The film-rating system's fallback scale — used ONLY when a night's own
 * `rating.scale_max` is absent (a deploy gap: an older-published payload
 * predating the fix round that added the field). `MoviePanel.jsx`'s own
 * fallback hardcodes the identical `5` for the identical reason, one seam
 * over (`Number.isFinite(Number(shared?.scale_max)) ? Number(shared.
 * scale_max) : 5`) — never the value read in the ordinary case, where
 * `utils/watch_projection.py::_movie_nights_view` publishes the bot's own
 * `utils.projection_publisher.SCALE_MAX` on every night's own `rating`. */
const MOVIE_NIGHT_RATING_SCALE_FALLBACK = 5

/** A movie-night `date` (a bare `YYYY-MM-DD` calendar value, never an
 * instant — `utils/watch_manager.py::_validate_movie_night_entry`'s own
 * docstring) formatted the SAME way `watchMember.js::watchedWhenLabel`
 * already formats every other bare-calendar completion date on this site
 * (reused verbatim, never re-implemented — gotcha 21). `null` for a night
 * with no date at all ("never invents one," the brief's own rule for the
 * night's `number`, applied identically here). */
function movieNightDateLabel(date) {
  return typeof date === 'string' && date !== '' ? watchedWhenLabel({ precision: 'day', value: date }) : null
}

/**
 * The room's verdict for one movie-night entry, in the SAME two-part
 * phrase `MoviePanel.jsx`'s own `VerdictBlock` uses for an identical
 * average/vote pair — measured, not assumed:
 * `site/src/test/portal/portal.test.jsx` asserts that block's own output
 * as `'4.3 out of 5'` for `average: 4.33`. `MoviePanel.jsx` exports no
 * shared helper for it (and is outside this slice's ownership regardless),
 * so this is a second, small implementation of the identical phrase —
 * pinned against the first by a shared-input test in
 * `watch-title.test.jsx` rather than assumed to agree (CLAUDE.md gotcha
 * 21). `null` when the night has no rating at all.
 *
 * A rating that is not yet `final` is labelled "so far," Jordan's own
 * ruling for a live poll's number (P6-303b brief §2.2) — the site's movie
 * page does not currently draw this distinction anywhere (measured: zero
 * reads of a rating's own `final` field across `site/src/**` before this
 * slice), so this is new wording this slice introduces, not a match of
 * existing site text.
 *
 * Fix round (manager review): the scale is READ off `rating.scale_max`,
 * never hardcoded — `MoviePanel.jsx`'s own identical fallback pattern,
 * applied to this payload's own field instead of the movie blob's
 * `shared.scale_max`.
 */
function movieNightRatingText(rating) {
  if (!rating || typeof rating !== 'object') return null
  const average = rating.average
  const votes = Number(rating.votes)
  const scaleMax = Number.isFinite(Number(rating.scale_max))
    ? Number(rating.scale_max)
    : MOVIE_NIGHT_RATING_SCALE_FALLBACK
  const parts = []
  if (average !== null && average !== undefined && Number.isFinite(Number(average))) {
    parts.push(`${Number(average).toFixed(1)} out of ${scaleMax}`)
  }
  if (Number.isFinite(votes) && votes > 0) {
    parts.push(`${votes} vote${votes === 1 ? '' : 's'}`)
  }
  if (parts.length === 0) return null
  const text = parts.join(' · ')
  return rating.final === false ? `${text} so far` : text
}

/**
 * One night's own line — brief §2.2's worked example, adapted (see
 * `movieNightRatingText`'s header for the verdict wording, and the note
 * below for the number). Omits any part the night does not have rather
 * than inventing one: a night missing its number, date or picker reads as
 * a shorter line, never as a blank or a guess.
 *
 * 🔴 THE NUMBER IS NOT DRAWN AS A ROMAN NUMERAL, AND THAT IS DELIBERATE —
 * `ActivityFeed.jsx`'s own header records why, for the identical field:
 * `number` is the bot's `roman_count`, an INTEGER, and the crew's one true
 * spelling of it is `movie_night_roman()`'s Roman-numeral output in the
 * Discord embed. Printing the plain int here would be a second, silently
 * disagreeing spelling of that same fact, and building a Roman-numeral
 * CONVERTER on the site would be a second IMPLEMENTATION of one this
 * codebase has already found two disagreeing versions of
 * (`cogs/movie/helpers.py::movie_night_roman`'s own docstring table) — the
 * identical `int_to_roman`-vs-`movie_night_roman` trap ActivityFeed.jsx
 * was written to avoid. This shows the plain count as "Movie Night #N," a
 * visibly different, site-native spelling nobody could mistake for the
 * embed's own numeral, omitted entirely (bare "Movie Night") when `number`
 * is absent.
 */
function MovieNightRow({ night, directory }) {
  const numberLabel = Number.isInteger(night?.number) ? `Movie Night #${night.number}` : 'Movie Night'
  const dateLabel = movieNightDateLabel(night?.date)
  const pickerId = joinableId(night?.picker_id)
  const verdict = movieNightRatingText(night?.rating)

  return (
    <li className="d2d-movienights__row" data-testid="movie-night-row">
      <span className="d2d-movienights__icon" aria-hidden="true">
        🎬
      </span>{' '}
      <span className="d2d-movienights__text">
        {numberLabel}
        {dateLabel ? ` · ${dateLabel}` : ''}
        {pickerId ? (
          <>
            {' · picked by '}
            <Person directory={directory} userId={pickerId} />
          </>
        ) : null}
        {verdict ? ` · the room: ${verdict}` : ''}
      </span>
    </li>
  )
}

/** The film's whole movie-night history, oldest night first — the bot's
 * own stored order (`_movie_nights_view`'s docstring: never re-sorted).
 * `null` (renders nothing) for a film never shown at movie night, which
 * is every non-movie title and most movies. */
function MovieNightsSection({ nights, directory }) {
  const list = Array.isArray(nights) ? nights : []
  if (list.length === 0) return null
  return (
    <section className="d2d-watch__section" data-testid="movie-nights">
      <span className="d2d-micro d2d-watchtitle__sectiontitle">At movie night</span>
      <ul className="d2d-movienights__list">
        {list.map((night, index) => (
          <MovieNightRow key={night?.week ?? index} night={night} directory={directory} />
        ))}
      </ul>
    </section>
  )
}

/** `key={subscriptionId}` on the wrapper below is what makes this reset on
 * every id change: season selection, revealed-episode set and the copy
 * confirmation are all local `useState` inside `WatchTitleBody`, and without
 * a key forcing a remount, following a `<Link>` from one title's page
 * straight to another's (same route pattern, so react-router reuses the
 * component instance) would carry the first title's open season and
 * revealed rows onto the second title's page. */
export default function WatchTitle() {
  const { subscriptionId } = useParams()
  return <WatchTitleBody key={subscriptionId} subscriptionId={subscriptionId} />
}

/**
 * Manager review round 2: the group's progress lines now follow the SAME
 * season selection as the episode list's own chips (the prototype's own
 * `sel`, shared by `titleVals()`'s lanes and its episode list) rather than
 * independently re-deriving "the group's current season" — a member not
 * in the season on screen now reads honestly against WHATEVER season is
 * showing, not always the group's.
 *
 * The state has to live here, above both `SeasonsSection` (the episode
 * list) and `WhereEveryoneIsSection` (the progress lines) — but this
 * component has several EARLY RETURNS before `detail`/`subscription` are
 * known (LOADING, NOT_FOUND, ...), and hooks cannot be called
 * conditionally. `SEASON_UNSET` is a sentinel `useState` can hold from the
 * very first render, before there is any season to default to; the real
 * default (`defaultSeasonNumber`, unchanged) is computed once `detail` is
 * in hand, in the READY branch below, and used only until the member
 * picks a season chip of their own.
 */
function WatchTitleBody({ subscriptionId }) {
  const title = useWatchTitle(subscriptionId)
  const directory = useDirectory()
  const now = useNow()
  const [selectedSeasonState, setSelectedSeasonState] = useState(SEASON_UNSET)
  // Round 2 ("no dead ends"): the ONLY state that needs this. The READY
  // path still gets `you` for free from the titles endpoint's own payload
  // (`functions/api/watch/titles/[subscriptionId].js`'s own header on why —
  // unchanged, still true, still one fewer `useSession()` call there). A
  // 404-turned-`ARCHIVED` response carries no such field (it is a 404, not
  // JSON), so this is the one branch that reads `session.userId` instead.
  const session = useSession()

  useDocumentTitle(
    title.state === READY
      ? title.subscription.title
      : title.state === ARCHIVED
        ? title.archiveTitle.title
        : 'Watch',
  )

  if (title.state === LOADING) {
    return (
      <article className="d2d-page" data-testid="watch-title-page">
        <p className="d2d-notice" data-testid="watch-title-loading">
          Loading the title…
        </p>
      </article>
    )
  }

  if (title.state === ARCHIVED) {
    return (
      <ArchivedTitleView
        archiveTitle={title.archiveTitle}
        directory={directory}
        userId={session.status === SIGNED_IN ? session.userId : null}
      />
    )
  }

  if (title.state === NOT_FOUND) {
    return (
      <article className="d2d-page" data-testid="watch-title-page">
        <p className="d2d-notice" data-testid="watch-title-not-found">
          That title is not on the list. It may have been removed, or the link is wrong.
        </p>
        <p>
          <Link to="/">← Watch</Link>
        </p>
      </article>
    )
  }

  if (title.state === MISCONFIGURED) {
    return (
      <article className="d2d-page" data-testid="watch-title-page">
        <p className="d2d-notice" data-testid="watch-title-misconfigured">
          Watch together is set up wrong on this deployment, so this title cannot be shown until
          that is fixed — this is not about your account.
        </p>
      </article>
    )
  }

  if (title.state !== READY) {
    return (
      <article className="d2d-page" data-testid="watch-title-page">
        <p className="d2d-notice" data-testid="watch-title-unavailable">
          This title is unavailable right now. Try again in a minute.
        </p>
      </article>
    )
  }

  const { subscription, detail, detailFailure, you } = title
  const userWho = deriveYou(subscription, you)
  const yourProgress = deriveYourProgress(subscription, you)
  const seasons = detail !== null ? seasonsOf(detail) : []
  // The member's own pick wins once they have made one; until then, the
  // group's own current season (`defaultSeasonNumber`, unchanged) is the
  // default BOTH the episode list and the progress lines start on.
  const selectedSeason = selectedSeasonState === SEASON_UNSET
    ? defaultSeasonNumber(subscription, seasons)
    : selectedSeasonState
  // P6-298e2: the season the chips show as active, as ONE answer for the page
  // (`activeSeasonOf`), which the hero's poster reads -- see `HeroPoster`.
  const activeSeason = activeSeasonOf(seasons, selectedSeason)

  return (
    <article className="d2d-page d2d-watch d2d-watchtitle" data-testid="watch-title-page">
      <p className="d2d-watchtitle__back">
        <Link to="/">← Watch</Link>
      </p>

      <Hero
        subscription={subscription}
        directory={directory}
        you={userWho}
        now={now}
        detail={detail}
        onChanged={title.confirm}
        season={activeSeason}
      />

      <SeasonTabStrip series={title.series} currentSeasonId={subscription.id} />

      <div className="d2d-watchtitle__grid">
        <SeasonsSection
          subscription={subscription}
          detail={detail}
          detailFailure={detailFailure}
          yourProgress={yourProgress}
          userId={you}
          directory={directory}
          onChanged={title.confirm}
          selectedSeason={selectedSeason}
          onSelectSeason={setSelectedSeasonState}
        />

        <div className="d2d-watchtitle__side">
          <MovieNightsSection nights={detail?.movie_nights} directory={directory} />
          <WhereEveryoneIsSection
            subscription={subscription}
            detail={detail}
            yourProgress={yourProgress}
            userId={you}
            you={userWho}
            directory={directory}
            onChanged={title.confirm}
            selectedSeason={selectedSeason}
            seasons={seasons}
          />
          <SeasonNewsSection subscription={subscription} />
          <ThreadsSection subscription={subscription} />
          <ReviewsSection
            subscription={subscription}
            directory={directory}
            userId={you}
            viewerFinished={userWho.status === TARGET_WATCHED}
          />
        </div>
      </div>

      <ManagePanel
        subscription={subscription}
        series={title.series}
        you={userWho}
        onChanged={title.confirm}
      />
    </article>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-2b ROUND 2 — THE ARCHIVED VIEW ("no dead ends"). A title off the
// watchlist, still in the archive: the poster, title and year, each
// member's status and rating, the SAME `ReviewsSection` the listed page
// uses (blur and didn't-finish box included), and one plain line saying
// it's off the watchlist. NO episode list and NO controls — this view
// never asks the bot for anything, the same way the read-only archive page
// itself never does.
// ═══════════════════════════════════════════════════════════════════════════

/** `watched`/`watching`/`stopped` → the same three words the archive page's
 * own status line uses, without the rating/position suffix that line adds
 * for the VIEWER specifically — this lists every member alike. */
function archivedStatusLabel(status) {
  if (status === TARGET_WATCHED) return 'Finished'
  if (status === 'watching') return 'Watching'
  if (status === TARGET_STOPPED) return 'Stopped'
  return null
}

function ArchivedMembersSection({ archiveTitle, directory }) {
  const members = Array.isArray(archiveTitle?.members) ? archiveTitle.members : []
  return (
    <section className="d2d-watch__section" data-testid="archived-members">
      <span className="d2d-micro d2d-watchtitle__sectiontitle">Who's marked in</span>
      {members.length === 0 ? (
        <p className="d2d-watch__flatline">Nobody has marked in.</p>
      ) : (
        <ul className="d2d-archivedmembers__list">
          {members.map((member, index) => {
            const id = joinableId(member.id)
            const label = archivedStatusLabel(member.status)
            const hasRating = typeof member.rating === 'number' && Number.isFinite(member.rating)
            // P6-303b: a completion made AT movie night reads as such,
            // wherever this page shows a member's history — only when the
            // status is a real finish; `completed_via` is meaningless on a
            // watching/stopped row (`utils/watch_manager.py::
            // record_movie_night` only ever sets it alongside
            // `MEMBER_STATUS_WATCHED`). The status condition stays here
            // (this call site's own job); `completedAtMovieNight` (fix
            // round, manager review — shared with `watchMember.js::
            // memberLogEntries` rather than a second copy of the check)
            // decides only the provenance half.
            const atMovieNight = member.status === TARGET_WATCHED
              && completedAtMovieNight(member)
            return (
              // eslint-disable-next-line react/no-array-index-key -- an id
              // that fails `joinableId` has nothing stable to key on; the
              // archive's own row order is otherwise stable per render.
              <li key={id ?? index} className="d2d-archivedmembers__row" data-testid="archived-member-row">
                <span className="d2d-archivedmembers__name">
                  {id ? <Person directory={directory} userId={id} /> : UNKNOWN_NAME}
                </span>
                <span className="d2d-micro d2d-watch__note">
                  {label ?? 'No status'}
                  {hasRating ? ` · ${member.rating}/10` : ''}
                  {atMovieNight ? ' · at movie night' : ''}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

function ArchivedTitleView({ archiveTitle, directory, userId }) {
  const mine = yourArchiveEntry(archiveTitle, userId)
  const viewerFinished = mine?.status === TARGET_WATCHED
  const hue = hueForTitle(archiveTitle)

  return (
    <article className="d2d-page d2d-watch d2d-watchtitle" data-testid="watch-title-page">
      <p className="d2d-watchtitle__back">
        <Link to="/watch/archive">← Archive</Link>
      </p>

      <section
        className="d2d-watchtitle__hero"
        data-testid="watch-title-archived-hero"
        style={{ '--d2d-title-hue': hue }}
      >
        <PosterSlot subscription={archiveTitle} size="hero" />
        <div className="d2d-watchtitle__herobody">
          <h1 className="d2d-watchtitle__h1">{archiveTitle?.title}</h1>
          <div className="d2d-watch__meta d2d-micro">
            {kindLabel(archiveTitle?.kind)}
            {Number.isInteger(archiveTitle?.year) ? ` · ${archiveTitle.year}` : ''}
          </div>
          <p className="d2d-watch__flatline" data-testid="archived-off-watchlist-note">
            This title is off the watchlist. Here is what the crew recorded about it.
          </p>
        </div>
      </section>

      <div className="d2d-watchtitle__side">
        <MovieNightsSection nights={archiveTitle?.movie_nights} directory={directory} />
        <ArchivedMembersSection archiveTitle={archiveTitle} directory={directory} />
        <ReviewsSection
          subscription={archiveTitle}
          directory={directory}
          userId={userId}
          viewerFinished={viewerFinished}
        />
      </div>
    </article>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// P6-298b — THE SEASON TAB STRIP
// ═══════════════════════════════════════════════════════════════════════════
//
// P6-318 — A SHOW IS ONE UNIT: every season of a show on the list IS on the list,
// so every tab below leads to an ordinary title page (the READY state, above).
// There used to be a second page here for a season that was linked but not
// listed, with a sentence saying so; the bot no longer has such a season, and
// the page and the sentence were removed with it. An id the summary does not
// list is the ordinary "That title is not on the list" now (or, when the
// archive has it, `ARCHIVED`).
//
// `seriesTabsFor` (`watch.js`) already returns `[]` for a series of one — the
// deploy-gap fallback AND the "one season" case collapse to the identical,
// no-strip answer, so `SeasonTabStrip` needs no extra guard of its own for
// either.

/**
 * The season tab strip — brief §2.2. Reuses `WatchScreenTabs.jsx`'s own
 * pattern (the brief's explicit instruction: "reuse the tab style the site
 * already has") rather than inventing a new one: a `role=tablist`/`role=tab`
 * strip that NAVIGATES between separate routes, the current one a plain,
 * unlinked `<span>` and every other one a real `<Link to="/watch/<id>">`.
 *
 * Each tab's ACCESSIBLE NAME is the season's FULL title (`aria-label`,
 * brief: "The full title is always the tab's accessible name"), independent
 * of the shortened `label` text and the decorative poster thumbnail drawn
 * inside it.
 */
function SeasonTabStrip({ series, currentSeasonId }) {
  const tabs = seriesTabsFor(series, currentSeasonId)
  if (tabs.length === 0) return null

  return (
    <div
      className="d2d-watchtitle__seasontabs"
      role="tablist"
      aria-label="Seasons"
      data-testid="series-season-tabs"
    >
      {tabs.map((tab) => {
        const content = (
          <>
            <PosterSlot subscription={{ poster_url: tab.posterUrl }} size="seasontab" />
            <span className="d2d-micro d2d-watchtitle__seasontablabel">{tab.label}</span>
          </>
        )
        return tab.isCurrent ? (
          <span
            key={tab.id}
            role="tab"
            aria-selected="true"
            aria-label={tab.title}
            className="d2d-watchtitle__seasontab d2d-watchtitle__seasontab--active"
            data-testid="series-season-tab"
          >
            {content}
          </span>
        ) : (
          <Link
            key={tab.id}
            to={`/watch/${tab.id}`}
            role="tab"
            aria-selected="false"
            aria-label={tab.title}
            className="d2d-watchtitle__seasontab"
            data-testid="series-season-tab"
          >
            {content}
          </Link>
        )
      })}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// THE HERO — 124px glowing poster, standing strip, H1, mono meta, next-up
// ═══════════════════════════════════════════════════════════════════════════
//
// V2-8a: the wash and the poster's glow are both driven by `hueForTitle`
// (`watch.js`) through ONE CSS custom property, `--d2d-title-hue`, set
// once on this section's own root — `.d2d-watch__poster--hero`'s own glow
// rule (app.css) reads the SAME variable by INHERITANCE (a CSS custom
// property is inherited by default), so `PosterSlot` itself needed no new
// prop to pick up the tint. A real poster still renders as the poster
// (`PosterSlot`, unchanged); the hue only tints what surrounds it — the
// brief's own words.
//
// P6-274: the synopsis slot Handoff §5 draws beneath the meta line
// ("An American football coach is hired to manage an English football
// club…"). Was a documented gap (docs/reports/W3b.md finding 1) — the
// provider layer fetched `TitleDetails.synopsis` on every `/watch info`
// call but never wrote it into `last_seen`, so nothing published carried
// one. `cogs/watch/providers.py::ProviderSnapshot.synopsis` closes that;
// `subscription.synopsis` is `null` for a subscription never refreshed or
// one whose provider genuinely has none (AniList sometimes, a film
// sometimes) — the honest-absence case renders no slot at all, never an
// empty box (see `HeroSynopsis` below).
//
// 🔴 This is the one field on this page that can itself be a SPOILER —
// TMDB/AniList overviews routinely describe events. The title page is a
// deliberate destination (the same shape `/watch info` already is in
// Discord), which is why it belongs here; it must never be threaded into
// anything ambient. `WatchPanel.jsx`'s own card never reads `.synopsis`
// either — see `tests/test_watch_helpers.py::TestSynopsisNeverReachesDiscord`
// (Python side) for the pinned negative.
function Hero({ subscription, directory, you, now, detail, onChanged, season }) {
  const stale = isStale(subscription, now)
  const seasonCount = detail !== null ? seasonsOf(detail).length : null
  const providers = Array.isArray(subscription?.providers) ? subscription.providers : []
  const asOfMs = instantOf(subscription?.providers_as_of)
  const old = providersOld(subscription, now)
  const hue = hueForTitle(subscription)

  return (
    <section
      className="d2d-watchtitle__hero"
      data-testid="watch-title-hero"
      style={{ '--d2d-title-hue': hue }}
    >
      <HeroPoster subscription={subscription} season={season} />
      <div className="d2d-watchtitle__herobody">
        <StandingStrip subscription={subscription} directory={directory} you={you} />
        <h1 className="d2d-watchtitle__h1">{subscription.title}</h1>
        <div className="d2d-watch__meta d2d-micro">
          {kindLabel(subscription?.kind)} · {sourceLabel(subscription?.kind)}
          {Number.isInteger(seasonCount) && seasonCount > 0
            ? ` · ${seasonCount} season${seasonCount === 1 ? '' : 's'}`
            : ''}
          {' · added by '}
          {addedById(subscription) ? (
            <Person directory={directory} userId={addedById(subscription)} />
          ) : (
            'someone in the crew'
          )}
        </div>

        <HeroSynopsis subscription={subscription} />

        {subscription.next_up ? (
          <HeroNextUp subscription={subscription} now={now} stale={stale} />
        ) : (
          <WhereThingsStandBlock subscription={subscription} />
        )}
        <FranchiseNote subscription={subscription} now={now} you={you} onChanged={onChanged} />

        <div className="d2d-watchtitle__herorow">
          <div className="d2d-watch__label d2d-micro">Where to watch</div>
          {providers.length > 0 ? (
            <div className="d2d-watch__provchips" data-testid="hero-providers">
              {providers.map((provider, index) => (
                <a
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${provider.name}-${index}`}
                  className="d2d-watch__provchip"
                  href={subscription.providers_link ?? undefined}
                  target="_blank"
                  rel="noreferrer"
                  aria-disabled={subscription.providers_link ? undefined : 'true'}
                >
                  <ProviderLogo url={provider.logo_url} />
                  <span>{provider.name}</span>
                  <span className="d2d-micro">{provider.kind?.toUpperCase()}</span>
                </a>
              ))}
            </div>
          ) : (
            <p className="d2d-watch__noprov">
              {typeof subscription?.providers_label === 'string' && subscription.providers_label
                ? subscription.providers_label
                : 'Not checked yet.'}
            </p>
          )}
          {asOfMs === null ? null : (
            <div className={`d2d-micro d2d-watch__note${old ? ' d2d-watch__note--old' : ''}`}>
              {old ? 'MAY HAVE MOVED · ' : ''}STREAMING LIST CHECKED {shortEasternDate(asOfMs)}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

/**
 * The hero's poster — P6-298e2. A TMDB show is ONE record whose seasons are
 * entries in `detail.seasons`, and each carries its own `poster_url` (the bot's
 * nightly writes it: `cogs/watch/refresh.py::_with_tmdb_season_posters`), so
 * the poster is the ACTIVE season's own — the one the chips show (`season`, from
 * `activeSeasonOf`). The chips themselves stay text chips (HANDOFF §5).
 *
 * The show's own poster is the fallback, and it is what draws: before there is
 * any detail to read (not published yet, or the read failed), for a season TMDB
 * gave none (`null`) or one published before the nightly filled it in (no key
 * at all), and for a URL `imageUrl` refuses (a refused URL is "none", not "a
 * broken one" — and never an empty hero while the show has a poster).
 *
 * An AniList title takes that fallback every time, by construction and not by a
 * branch: each of its seasons is its OWN record with its own poster, so its page
 * already IS that season's poster, and the one season entry it carries has no
 * `poster_url`. Moving between its seasons is a navigation (`SeasonTabStrip`),
 * which remounts the whole page. Same component, same `PosterSlot`; the two kinds
 * of show differ only in what feeds it, so there is nothing to share beyond that.
 *
 * `key` is load-bearing. `PosterSlot` remembers a failed load in its own state,
 * and this slot now changes URL without remounting: without the key, one
 * season's broken image would draw "no poster" for every season after it.
 */
function HeroPoster({ subscription, season }) {
  const url = imageUrl(season?.poster_url) ?? subscription?.poster_url ?? null
  return <PosterSlot key={url ?? 'none'} subscription={{ poster_url: url }} size="hero" />
}

// P6-274: honest-absence, not an empty box — a subscription never
// refreshed yet, or whose provider genuinely has no synopsis (AniList
// sometimes, a film sometimes — see `ProviderSnapshot.synopsis`'s own
// docstring), renders no slot at all rather than an empty paragraph or a
// placeholder sentence TMDB/AniList never gave the bot.
function HeroSynopsis({ subscription }) {
  const synopsis = typeof subscription?.synopsis === 'string' ? subscription.synopsis.trim() : ''
  if (!synopsis) return null
  return (
    <p className="d2d-watchtitle__synopsis" data-testid="hero-synopsis">
      {synopsis}
    </p>
  )
}

function HeroNextUp({ subscription, now, stale }) {
  const nextUp = subscription.next_up
  const code = episodeCode(nextUp.season, nextUp.episode)
  const exact = isExactCountdown(subscription)
  const airsAtMs = instantOf(nextUp.airs_at)
  const countdown = countdownFor(subscription, now)

  return (
    <div className="d2d-watchtitle__herorow" data-testid="hero-next-up">
      <div className="d2d-watch__nextup">
        <div className="d2d-micro d2d-watch__label">
          {stale ? 'LAST KNOWN — NOT CONFIRMED' : 'NEXT UP'}
        </div>
        <div className="d2d-watch__nextline">
          {code ? `${code} · ` : ''}
          {airsAtMs === null ? 'date unknown' : weekdayEasternDate(airsAtMs)}
        </div>
        <div className="d2d-micro d2d-watch__note">
          {exact && airsAtMs !== null
            ? `${easternClock(airsAtMs)} ET · EXACT · FROM ANILIST`
            : 'NO AIR TIME PUBLISHED · CHECKED FROM 21:00 ET'}
        </div>
        {stale ? null : (
          <div className="d2d-watch__count" data-testid="hero-countdown">
            {isTonight(subscription, now) ? (
              <span className="d2d-watch__tonightdot" aria-hidden="true" />
            ) : null}
            <span>in {countdown?.text}</span>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * V2-6b, brief §2 items 4 and 5 — TWO mutually exclusive notes, additional
 * to `HeroNextUp`/`WhereThingsStandBlock` above, never a replacement of
 * either. The row's own Next-line variant (`RowNextAwaiting`,
 * `WatchPanel.jsx`) is the only thing V2-6b changes about what a Watching
 * row shows; the title page gains new information instead.
 *
 * - **Announced** (item 4): `awaiting` names a linked sequel or a scheduled
 *   TMDB season — shown whether the title itself is still airing (Watching,
 *   `apo2`'s own case: the row keeps its ordinary next-episode line, and
 *   this is the ONLY place the announcement appears) or has already moved
 *   to the Awaiting shelf (where `HeroNextUp` never fires — `next_up` is
 *   null by construction whenever this case applies — so this is the only
 *   place on the page naming what is coming). `awaiting`'s "third shape"
 *   (never aired at all) never reaches this branch: `awaitingBaseText`
 *   only returns a sequel's title or a season number when one of those two
 *   fields is actually set, which is exactly this branch's own gate.
 * - **Not found** (item 5, spec §5 verbatim, V2-6b): a FINISHED title with
 *   nothing at all known about what comes next — `noNextSeasonFound`
 *   (`watch.js`).
 *
 * 🔴 Round 2 (manager ruling): NEVER future tense for a past date.
 * `utils/watch_projection.py::_sequel_awaits` treats AniList's
 * `NOT_YET_RELEASED` as awaiting regardless of the date on file, because
 * AniList's own status can lag a real premiere (`isPastInstant`'s own
 * docstring) — so `awaiting.next_air` can genuinely be a PAST instant while
 * this branch still fires. Saying "premiering `<a past day>`" is exactly
 * the bug V2-6a round 3 already fixed once, on the bot's own announcement
 * line (`_availability_phrase`'s past-instant safety net) — this is the
 * site's half of the identical fix: `isPastInstant` decides the verb,
 * never the mere presence of a date.
 *
 * 🔴 V2-6h2 — THE OLD "NOT FOUND" BRANCH IS GONE; `FranchiseLinkControl`
 * REPLACES IT, AND DRAWS A THIRD STATE THE TWO BRANCHES ABOVE NEVER COVERED.
 * The **announced** branch above is UNCHANGED, on purpose (this slice's own
 * brief: "What must not change: FranchiseNote's announced branch"). What
 * used to be item 5's own early return (naming `/watch link` in prose) is
 * now `<FranchiseLinkControl>`, mounted unconditionally right after — it
 * re-derives `noNextSeasonFound` itself and renders nothing unless that is
 * true OR `subscription.sequel` names a real link (V2-6d), which is
 * INDEPENDENT of `announced`: a linked sequel already RELEASING/FINISHED
 * reads `awaiting: null` (so `announced` is false) while `sequel` still
 * names it — see that component's own header for why both facts can be
 * true together, and why they render as two separate lines rather than one.
 */
function FranchiseNote({ subscription, now, you, onChanged }) {
  const awaiting = subscription?.awaiting ?? null
  const announced = awaiting != null && (awaiting.next_id != null || awaiting.season_number != null)
  const base = announced ? awaitingBaseText(subscription) : null
  const airsAtMs = announced ? instantOf(awaiting?.next_air ?? null) : null

  return (
    <>
      {announced ? (
        <p className="d2d-watch__flatline" data-testid="franchise-announced">
          {airsAtMs === null
            ? `${base} is announced — no date yet.`
            : isPastInstant(airsAtMs, now)
              ? `${base} is announced, due ${weekdayEasternDate(airsAtMs)}.`
              : `${base} is announced, premiering ${weekdayEasternDate(airsAtMs)}.`}
        </p>
      ) : null}
      <FranchiseLinkControl subscription={subscription} you={you} onChanged={onChanged} />
    </>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// SEASONS AND EPISODES — the left column
// ═══════════════════════════════════════════════════════════════════════════

// V2-8b: `defaultSeasonNumber` MOVED to `watch.js` — it never read anything
// title-page-specific (only a subscription's own derived group position and
// a list of `{number}` season entries), and the watchlist row now needs the
// identical "which season is current" answer. Imported above; unchanged.

/**
 * P6-283: one shared request, hoisted above every season/episode — "only
 * one request in flight at a time" across the WHOLE episode list, not per
 * row and not per season (switching the season chip while a tap is in
 * flight must not let a second one start). `activeKey` names which row
 * owns the CURRENT request's display (`episodeRevealKey`'s own format,
 * reused rather than a second key shape); `lastTargetRef` is
 * `MemberActionRow`'s exact pattern (`WatchPanel.jsx`) for the same reason
 * it exists there — `isConfirmed` needs to know what THIS press asked for,
 * and `effectiveTarget` (the single-target shape `ProgressStepper` reads)
 * does not apply here, where every row is its own possible target.
 */
function useEpisodeTapController(subscription, userId, onChanged) {
  const request = useWatchRequest()
  const [activeKey, setActiveKey] = useState(null)
  const lastTargetRef = useRef(null)

  const isConfirmed = (shared) => {
    const fresh = subscriptionsOf(shared).find((row) => row.id === subscription?.id)
    const mine = deriveYourProgress(fresh, userId)
    return (
      mine?.season === lastTargetRef.current?.season && mine?.episode === lastTargetRef.current?.episode
    )
  }
  const confirmFailed = useConfirmAfterApplied(request, onChanged, isConfirmed)

  const onTap = (season, episodeNumber) => {
    lastTargetRef.current = { season, episode: episodeNumber }
    setActiveKey(episodeRevealKey(season, episodeNumber))
    request.ask({
      kind: KIND_SET_PROGRESS,
      target: subscription.id,
      payload: { to: { season, episode: episodeNumber } },
    })
  }

  return { request, activeKey, onTap, confirmFailed }
}

/**
 * The season the chips show as active: the one `selected` names, or the first
 * entry when it names none that exists (a season TMDB has since merged away, or
 * `undefined` before there is a detail to pick from). **The ONE implementation
 * of that rule** — the page's episode list (`SeasonsSection`) and, since
 * P6-298e2, the hero's
 * poster (`WatchTitleBody` → `HeroPoster`) both read it, so they cannot name
 * different seasons (CLAUDE.md gotcha 21: two copies of
 * `seasons.find(...) ?? seasons[0]` agree today and not after the first edit
 * to one of them).
 */
function activeSeasonOf(seasons, selected) {
  return seasons.find((season) => (season.number ?? null) === selected) ?? seasons[0]
}

function SeasonsSection({
  subscription,
  detail,
  detailFailure,
  yourProgress,
  userId,
  directory,
  onChanged,
  selectedSeason: selected,
  onSelectSeason: setSelected,
}) {
  const seasons = detail !== null ? seasonsOf(detail) : []
  const [revealed, setRevealed] = useState(() => new Set())
  // V2-6g2: which season(s) a member has already chosen to expand past the
  // collapse threshold — keyed the same way the season chips key `React`'s
  // own `key` (`number ?? 'anime'`), so switching chips and back remembers
  // an expansion rather than re-collapsing it. One-way, like `revealed`
  // above: the brief asks for "Show all N episodes" to expand, never for a
  // way to re-collapse.
  const [expandedSeasons, setExpandedSeasons] = useState(() => new Set())
  const tap = useEpisodeTapController(subscription, userId, onChanged)

  const notFetchedYet = seasonsNotFetchedYet(subscription, detail)
  // Computed once, unconditionally, and shared between the season chips'
  // fallback and `SeasonBody`'s own expand state — two independent
  // `seasons.find(...) ?? seasons[0]` calls could disagree about which
  // season is actually showing the moment `selected` names one that no
  // longer exists (CLAUDE.md gotcha 21's shape, at a smaller scale). P6-298e2:
  // that answer is now `activeSeasonOf`'s, shared with the hero's poster too.
  const activeSeason = activeSeasonOf(seasons, selected)
  const activeSeasonKey = activeSeason?.number ?? 'anime'

  return (
    <SeasonsBox>
      {subscription?.kind === 'movie' ? (
        <p className="d2d-watch__flatline" data-testid="seasons-film">
          Films have no episodes.
        </p>
      ) : detail === null ? (
        <SeasonsDetailFailure detailFailure={detailFailure} />
      ) : notFetchedYet ? (
        <p className="d2d-watch__flatline" data-testid="seasons-waiting">
          This title has not been checked yet. The bot fills in seasons on its nightly pass, one
          season a night — nothing is wrong.
        </p>
      ) : (
        <>
          <SeasonChips seasons={seasons} selected={selected} onSelect={setSelected} />

          <SeasonBody
            subscription={subscription}
            season={activeSeason}
            revealed={revealed}
            onReveal={(key) => setRevealed((set) => new Set(set).add(key))}
            yourProgress={yourProgress}
            directory={directory}
            tap={tap}
            expanded={expandedSeasons.has(activeSeasonKey)}
            onExpand={() => setExpandedSeasons((set) => new Set(set).add(activeSeasonKey))}
          />

          <p className="d2d-watch__flatline d2d-watchtitle__note">
            Titles of unaired episodes are published early and can give the story away, so they
            stay hidden until you ask. The number and the date are always shown.
          </p>
        </>
      )}
    </SeasonsBox>
  )
}

/**
 * The "Seasons and episodes" box — one section shell and one title.
 */
function SeasonsBox({ children }) {
  return (
    <section className="d2d-watch__section" data-testid="watch-title-seasons">
      <div className="d2d-watchtitle__sectionhead">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">Seasons and episodes</span>
      </div>

      {children}
    </section>
  )
}

/** The season chips — one pressable chip per season entry. */
function SeasonChips({ seasons, selected, onSelect }) {
  return (
    <div className="d2d-watchtitle__chips">
      {seasons.map((season) => {
        const number = season.number ?? null
        const active = number === selected
        return (
          <button
            key={number ?? 'anime'}
            type="button"
            className={`d2d-watchtitle__chip${active ? ' d2d-watchtitle__chip--active' : ''}`}
            aria-pressed={active}
            data-testid="season-chip"
            onClick={() => onSelect(number)}
          >
            {seasonLabel(number)}
          </button>
        )
      })}
    </div>
  )
}

/** The brief's second and third failure states, kept distinguishable —
 * `detailFailure` travels from `functions/_lib/projections.js::
 * TITLE_DETAIL_FAILURE` unchanged, so this reads the SAME reason the
 * operator's log line names, never a re-guessed one. */
function SeasonsDetailFailure({ detailFailure }) {
  if (detailFailure === 'unpublished') {
    return (
      <p className="d2d-watch__flatline" data-testid="seasons-unpublished">
        This title has not been checked yet. The bot fills in seasons on its nightly pass, one
        season a night — nothing is wrong.
      </p>
    )
  }
  return (
    <p className="d2d-watch__flatline" data-testid="seasons-unavailable">
      The season and episode list is unavailable right now. Everything else on this page is
      unaffected.
    </p>
  )
}

/**
 * One season's episode list. (P6-298h's `readOnly` mode, which drew it for a
 * season that was not on the list, was removed by P6-318 with that page: a
 * show is one unit, so every season has a subscription and a crew position.)
 */
function SeasonBody({
  subscription, season, revealed, onReveal, yourProgress, directory, tap, expanded, onExpand,
}) {
  const episodes = episodesOf(season)
  const seasonNumber = season?.number ?? null

  if (episodes === null) {
    return (
      <p className="d2d-watch__flatline" data-testid="season-not-checked">
        This season has not been checked yet. Nothing is wrong.
      </p>
    )
  }
  if (episodes.length === 0) {
    return (
      <p className="d2d-watch__flatline" data-testid="season-empty">
        No episodes listed for this season yet.
      </p>
    )
  }

  // P6-283: who is AT the group's position, named once per season body
  // rather than once per row — the list itself never changes while this
  // component renders, only which row (if any) it belongs to.
  const leaderIds = groupLeaderIds(subscription)

  // V2-6g2: replaces the old, anime-only claim that AniList "publishes only
  // the most recently aired episode and the next one" — false since V2-6g
  // (a FINISHED or long-running anime's list can be the title's full episode
  // count). The truth generalises past anime and is read off the season's
  // FULL list, never a collapsed view's own visible slice, so a dateless row
  // hidden behind "Show all N episodes" still earns the note.
  const showDatelessNote = seasonHasUnknownAirDate(episodes)

  // V2-6g2: One Piece's ~1,179 aired episodes must not all render by
  // default (brief §2). `expanded` is per-season, one-way, lifted to
  // `SeasonsSection` — see that component's own comment on `expandedSeasons`.
  const shouldCollapse = episodes.length > EPISODE_COLLAPSE_THRESHOLD && !expanded
  const visibleRuns = shouldCollapse
    ? episodeRuns(collapsedEpisodeIndices(episodes, seasonNumber, subscription, yourProgress))
    : null

  // One row's markup, identical whichever view calls it — collapsed or
  // expanded, this is the ONLY place that builds an episode row, so P6-283's
  // tap (and everything else about a row) cannot drift between the two views
  // by construction rather than by two implementations staying in sync.
  const renderEpisodeWrap = (episode) => {
    const key = episodeRevealKey(seasonNumber, episode.number)
    const marker = isGroupPosition(seasonNumber, episode.number, subscription)
    const yours = isYourPosition(seasonNumber, episode.number, yourProgress)
    const state = episodeState(seasonNumber, episode.number, subscription)
    const isHidden = state === 'unaired' && !revealed.has(key)
    const threadUrl = threadUrlFor(subscription, seasonNumber, episode.number)
    // P6-283: "every AIRED episode row becomes a tap target" — the
    // brief's own words. `state` already tells the two apart (`unaired`
    // is the ONLY state an un-aired episode can carry — `episodeState`,
    // `watchTitle.js`), so this is not a second aired-ness derivation,
    // only a name for the branch below.
    const aired = state !== 'unaired'
    const isActiveRequest = tap.activeKey === key
    const rowStatus = isActiveRequest ? tap.request.status : null
    // V2-8a: the per-row rail — dusk for an episode YOU have seen,
    // grey (muted, never an accent — dusk is progress only) for one
    // the group has seen ahead of you. `seenByYou`/`seenByGroup` are
    // "at or before this position", never "exactly at it" (that is
    // `isYourPosition`/`isGroupPosition`, the marker's own job,
    // computed above) — a rail spans every episode already covered,
    // not just the one at the very edge of it.
    const seenByYou =
      aired &&
      Number.isInteger(yourProgress?.episode) &&
      comparePosition(
        { season: seasonNumber, episode: episode.number },
        { season: yourProgress.season ?? null, episode: yourProgress.episode },
      ) <= 0
    // No `!seenByYou` here — the render below is a ternary
    // (`seenByYou ? rail-you : seenByGroup ? rail-group : null`), so
    // that exclusion already lives in the ELSE branch. Folding it into
    // this variable too made an earlier draft's mutation unkillable
    // (the ternary's own branching already excluded it) — the same
    // shape `ActionBar.jsx`'s own `isMovie` redundancy took, and the
    // same fix: one gate, not two.
    const seenByGroup =
      aired &&
      Number.isInteger(subscription?.progress?.episode) &&
      comparePosition(
        { season: seasonNumber, episode: episode.number },
        { season: subscription.progress.season ?? null, episode: subscription.progress.episode },
      ) <= 0

    return (
      <div key={key} className="d2d-watchtitle__episodewrap">
        {yours ? (
          <div className="d2d-watchtitle__marker" data-testid="your-marker">
            <span className="d2d-watchtitle__markerlabel d2d-micro">You&rsquo;re here</span>
            <span className="d2d-watchtitle__markerline" aria-hidden="true" />
          </div>
        ) : null}
        {marker ? (
          <div className="d2d-watchtitle__marker" data-testid="group-marker">
            <span className="d2d-watchtitle__markerlabel d2d-micro">
              The group is here
              {
                // P6-281 publishes `leader_ids` but a pre-P6-281 (or
                // otherwise legacy-floor) fixture carries none —
                // `groupLeaderIds` already reads both as `[]`, so this
                // renders the exact, unnamed sentence the marker always
                // had rather than a broken lookup or an em dash to
                // nobody.
                leaderIds.length > 0 ? (
                  <>
                    {' — '}
                    <People directory={directory} ids={leaderIds} />
                  </>
                ) : null
              }
            </span>
            <span className="d2d-watchtitle__markerline" aria-hidden="true" />
          </div>
        ) : null}
        <div className="d2d-watchtitle__episoderow" data-testid="episode-row">
          {seenByYou ? (
            <span className="d2d-watchtitle__rail d2d-watchtitle__rail--you" aria-hidden="true" data-testid="rail-you" />
          ) : seenByGroup ? (
            <span className="d2d-watchtitle__rail d2d-watchtitle__rail--group" aria-hidden="true" data-testid="rail-group" />
          ) : null}
          {aired ? (
            <EpisodeTap
              subscription={subscription}
              season={seasonNumber}
              episode={episode}
              state={state}
              disabled={tap.request.busy}
              rowStatus={rowStatus}
              message={tap.request.message}
              confirmFailed={isActiveRequest ? tap.confirmFailed : null}
              onTap={tap.onTap}
            />
          ) : (
            <>
              <span className="d2d-watchtitle__epcode">
                {episodeCode(seasonNumber, episode.number)}
              </span>
              <span className="d2d-watchtitle__epdate d2d-micro">
                {episodeDateLabel(episode.air_date)}
              </span>
              <div className="d2d-watchtitle__eptitlewrap">
                {isHidden ? (
                  <button
                    type="button"
                    className="d2d-watchtitle__reveal"
                    data-testid="reveal-title"
                    onClick={() => onReveal(key)}
                  >
                    Show title
                  </button>
                ) : (
                  <span className="d2d-watchtitle__eptitle">
                    {typeof episode.title === 'string' && episode.title ? episode.title : '—'}
                  </span>
                )}
              </div>
              <span
                className="d2d-micro d2d-watchtitle__epstate"
                data-testid={`episode-state-${state}`}
              >
                Unaired
              </span>
            </>
          )}
          {threadUrl ? (
            <a
              className="d2d-watch__link"
              href={threadUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="episode-thread-link"
            >
              Thread ↗
            </a>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <div className="d2d-watchtitle__episodes">
      {showDatelessNote ? (
        <p className="d2d-micro d2d-watch__note" data-testid="episode-dateless-note">
          Air dates aren&rsquo;t known for some older episodes.
        </p>
      ) : null}
      {shouldCollapse
        ? visibleRuns.flatMap(([start, end], runIndex) => {
            const rows = []
            if (runIndex > 0) {
              // A plain, non-interactive gap between two windows that are
              // not adjacent episodes (brief §2: "the member's own position…
              // the latest aired few") — presentational only, so it is
              // `aria-hidden` rather than announced as a row.
              rows.push(
                <div
                  key={`gap-${start}`}
                  className="d2d-watchtitle__epgap"
                  data-testid="episode-gap"
                  aria-hidden="true"
                >
                  ⋯
                </div>,
              )
            }
            for (let i = start; i <= end; i += 1) rows.push(renderEpisodeWrap(episodes[i]))
            return rows
          })
        : episodes.map((episode) => renderEpisodeWrap(episode))}
      {shouldCollapse ? (
        <button
          type="button"
          className="d2d-watchtitle__showall"
          data-testid="episodes-show-all"
          onClick={onExpand}
        >
          {`Show all ${episodes.length} episodes`}
        </button>
      ) : null}
    </div>
  )
}

/**
 * One AIRED episode row's tap target — P6-283: "every aired episode row
 * becomes a tap target." The whole row is the `<button>` (44px+ tall by
 * construction — epcode/date/title stack exactly as they did before, just
 * inside a button rather than a bare div) so this is the biggest honest hit
 * target available, not a small icon bolted on. The thread link stays a
 * SIBLING outside this button, never nested inside it — an `<a>` inside a
 * `<button>` is invalid content and would break keyboard/screen-reader
 * navigation for both.
 *
 * `aria-label` is stable regardless of state ("I've seen up to S4E7") —
 * the brief's own phrase, made row-specific rather than the literal "here"
 * a screen reader would announce identically on every row in the list.
 * The VISIBLE content stays exactly what it was (epcode, date, title) plus
 * whichever trailing badge applies — Jordan's own instruction to build this
 * "clean and minimal, not bespoke" ahead of the Claude Design redesign, so
 * no new always-on label is added to every row for sighted readers.
 *
 * `disabled` is `tap.request.busy` UNCONDITIONALLY (every aired row, active
 * or not) — `ProgressStepper`/`MemberActionRow`'s own `disabled={request.busy}`
 * pattern (`WatchPanel.jsx`), reused rather than reinvented: "only one
 * request in flight at a time. ... other rows don't send" IS that pattern,
 * applied to many buttons sharing one request instead of one.
 */
function EpisodeTap({ subscription, season, episode, state, disabled, rowStatus, message, confirmFailed, onTap }) {
  const code = episodeCode(season, episode.number)
  const titleText = typeof episode.title === 'string' && episode.title ? episode.title : '—'
  const isFinale = isFinalEpisode(subscription, season, episode.number)

  return (
    <button
      type="button"
      className="d2d-watchtitle__eptap"
      data-testid="episode-tap"
      data-state={rowStatus ?? undefined}
      aria-label={`I've seen up to ${code}`}
      disabled={disabled}
      onClick={() => onTap(season, episode.number)}
    >
      <span className="d2d-watchtitle__epcode">{code}</span>
      <span className="d2d-watchtitle__epdate d2d-micro">{episodeDateLabel(episode.air_date)}</span>
      <span className="d2d-watchtitle__eptitlewrap">
        <span className="d2d-watchtitle__eptitle">{titleText}</span>
      </span>
      {rowStatus ? (
        // No `onRetry`/`request.reset` here — see ActStrip.jsx's own
        // header on why a nested Retry/OK button inside this already-a-
        // `<button>` row would be invalid: pressing the row again, once it
        // is no longer `disabled`, already re-sends the tap as a new
        // request.
        <ActStrip
          request={{ status: rowStatus, message }}
          confirmFailed={confirmFailed}
          testId="episode-request-status"
          confirmStaleTestId="episode-request-confirm-stale"
          // V2-4c: appends the design's own pointer to V2-8a's existing
          // lead-in, exactly as `ProgressStepper`'s identical branch does
          // (`WatchPanel.jsx`) — a substring append, never a restructure,
          // so this file's own mutation-tested `toHaveTextContent` checks
          // stay green (see that file's comment for the full reasoning).
          doneText={`You're on ${code}.${isFinale ? ' That was the final episode. Rate it if you like.' : ''}`}
          command={progressCommand(subscription)}
        />
      ) : (
        <span className="d2d-micro d2d-watchtitle__epstate" data-testid={`episode-state-${state}`}>
          {state === 'seen' ? 'Seen together' : 'Not yet'}
        </span>
      )}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-8a: "WHERE EVERYONE IS" — the group card, your standing box, the
// group's progress lines (or member chips), and the action bar. Replaces
// the old "Where we are" (group line + progress control) and "Who is in"
// (chip row + status control) sections, which drew the SAME facts split
// across two boxes with no shared visual language — HANDOFF.md draws them
// as one card. `title-where-we-are` is kept as this section's own testid
// (renamed in spirit, not in hook) precisely because it is the most
// heavily depended-on anchor in this file's own test suite.
// ═══════════════════════════════════════════════════════════════════════════

// V2-8b: `yourStanding` MOVED to `watch.js` — the watchlist row's own You
// box needs the identical six wordings (brief: "your You box... with
// V2-8a's standing wording"). Imported above; its call site below is
// unchanged.

/** The group's own line — `toWatchInfo`'s exact pre-v2 arithmetic and
 * wording, unchanged: this is a correctness constraint (Handoff), not a
 * style choice, and nothing about the redesign touches the numbers. */
function groupStandingLine(subscription) {
  const info = toWatchInfo(subscription)
  if (info.mode === 'caught_up') return `Group on ${info.at} — caught up`
  if (info.mode === 'behind') return `Group on ${info.progress} — ${info.count} to watch`
  if (info.mode === 'cross_season') {
    return info.endedOrCancelled
      ? `Group on ${info.progress} · ends at ${info.lastAired}`
      : `Group on ${info.progress} · ${info.lastAired} aired`
  }
  return 'Nothing set yet.'
}

/** One member's row in the group's progress lines — a 30px avatar, a
 * name, and a per-episode strip in the season the group is currently
 * relevant to (`seasonNumber`/`episodes`, computed once by the caller).
 * `isYou`: ember avatar ring and name, dusk bars — dusk is progress ONLY,
 * so every other member's own seen episodes read in a quiet, neutral
 * shade rather than either accent.
 *
 * V2-6g2 round 2: `visibleRuns` is `GroupProgressBlock`'s own collapse
 * (`groupProgressWindowIndices`/`episodeRuns`, `watchTitle.js`) — `null`
 * below the threshold, where every row renders exactly as it always has.
 * Above it, this renders only the given runs, with one plain gap between
 * runs that are not adjacent — the identical shape `SeasonBody`'s own
 * collapse uses, one seam over, so a member's own position is still an
 * ACTUAL block at that exact episode, never a rounded or interpolated one. */
function MemberProgressLine({ id, isYou, directory, progress, episodes, seasonNumber, subscription, visibleRuns }) {
  const person = nameFor(directory, id)
  const initial = person.known ? person.label.trim().charAt(0).toUpperCase() : '·'

  const renderBlock = (episode) => {
    const aired = episodeState(seasonNumber, episode.number, subscription) !== 'unaired'
    const cmp = Number.isInteger(progress?.episode)
      ? comparePosition(
          { season: seasonNumber, episode: episode.number },
          { season: progress.season ?? null, episode: progress.episode },
        )
      : null
    const seenByMember = cmp !== null && cmp <= 0
    const variant = !aired ? 'future' : seenByMember ? (isYou ? 'you' : 'seen') : 'aired'
    return (
      <span
        key={episode.number}
        className={`d2d-watchtitle__progressblock d2d-watchtitle__progressblock--${variant}`}
      />
    )
  }

  return (
    <div
      className={`d2d-watchtitle__progressline${isYou ? ' d2d-watchtitle__progressline--you' : ''}`}
      data-testid="progress-line"
    >
      <MemberLink to={memberProfilePath(id)} className="d2d-watchtitle__progresswho">
        <AvatarCircle person={person} initial={initial} size={30} />
        <span className={`d2d-watchtitle__progressname${isYou ? ' d2d-watchtitle__progressname--you' : ''}`}>
          {person.known ? person.label : UNKNOWN_NAME}
        </span>
      </MemberLink>
      <span className="d2d-watchtitle__progressstrip" aria-hidden="true">
        {visibleRuns
          ? visibleRuns.flatMap(([start, end], runIndex) => {
              const blocks = []
              if (runIndex > 0) {
                blocks.push(
                  <span key={`gap-${start}`} className="d2d-watchtitle__progressgap" data-testid="progress-gap" aria-hidden="true" />,
                )
              }
              for (let i = start; i <= end; i += 1) blocks.push(renderBlock(episodes[i]))
              return blocks
            })
          : episodes.map((episode) => renderBlock(episode))}
      </span>
    </div>
  )
}

/** The group's progress lines (a series with a fetched season) or, when
 * there is no episode track to draw at all (a movie, or seasons not
 * fetched yet), a plain member-chip fallback — HANDOFF's own `hasLanes`/
 * `hasOthers` mutual exclusion, over data this slice already has (the
 * title page's own `detail`, not the summary list's per-season gap
 * RECONCILIATION names for the ROWS). Members shown are exactly `watching`
 * and `watched` — `stopped` gets its own quiet line beside this block, the
 * same "not counted among who is watching" rule the pre-v2 sections
 * already used.
 *
 * Manager review round 2: `selectedSeason` and `seasons` are now the
 * CALLER's own (`WatchTitleBody`'s lifted state, shared with the episode
 * list's own chips) rather than independently re-derived here — the
 * prototype's own `titleVals()` draws its lanes for `sel`, the SAME season
 * variable its episode list uses, never the group's position recomputed a
 * second time. A member whose own position is in a different season than
 * `selectedSeason` reads honestly through the ordinary per-episode
 * comparison in `MemberProgressLine` below: an EARLIER season compares as
 * "at or before" every episode in view (all seen), a LATER one compares as
 * "after" all of them (not started) — `comparePosition`'s own season-first
 * ordering already gives this for free, with no special case needed here. */
function GroupProgressBlock({ subscription, seasons, selectedSeason, directory, userId }) {
  const isMovie = subscription?.kind === 'movie'
  const season = !isMovie ? seasons.find((s) => (s.number ?? null) === (selectedSeason ?? null)) ?? null : null
  const episodes = season ? episodesOf(season) : null
  const canDrawLines = !isMovie && Array.isArray(episodes) && episodes.length > 0

  const rawMembers = Array.isArray(subscription?.members) ? subscription.members : []
  const shown = rawMembers
    .filter((m) => m.status === 'watching' || m.status === 'watched')
    .map((m) => ({ id: typeof m.id === 'string' ? m.id : null, progress: m.progress }))
    .filter((m) => m.id !== null)

  if (shown.length === 0) return null

  // V2-6g2 round 2: computed ONCE, here, and handed to every member's row —
  // never once per row — so a given horizontal position means the same
  // episode in every line (comparing members is this section's whole job).
  // Same threshold as `SeasonBody`'s own collapse: a season short enough
  // for one view is short enough for the other.
  const shouldCollapseLines = canDrawLines && episodes.length > EPISODE_COLLAPSE_THRESHOLD
  const visibleRuns = shouldCollapseLines
    ? episodeRuns(
        groupProgressWindowIndices(
          episodes,
          selectedSeason ?? null,
          subscription,
          shown.map((m) => m.progress),
        ),
      )
    : null

  // HANDOFF's own header: "Everyone · Season N" when the title has more
  // than one season, "Everyone · N episodes" for a single season or an
  // anime — never a season SUFFIX on the plain chip fallback, which the
  // design draws as a bare "Everyone" (nothing to name a season of).
  const laneLabel = canDrawLines
    ? seasons.length > 1
      ? seasonLabel(selectedSeason ?? null)
      : `${episodes.length} episode${episodes.length === 1 ? '' : 's'}`
    : null

  if (!canDrawLines) {
    return (
      <div data-testid="member-chips-block">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">Everyone</span>
        <div className="d2d-watch__whorow">
          {shown.map((m) => {
            const person = nameFor(directory, m.id)
            const initial = person.known ? person.label.trim().charAt(0).toUpperCase() : '·'
            return (
              <MemberLink
                key={m.id}
                to={memberProfilePath(m.id)}
                className={`d2d-watch__memberchip${m.id === userId ? ' d2d-watch__memberchip--you' : ''}`}
                testId="who-is-in-chip"
              >
                <AvatarCircle person={person} initial={initial} size={24} />
                <Person directory={directory} userId={m.id} />
              </MemberLink>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div data-testid="progress-lines-block">
      <div className="d2d-watchtitle__sectionhead">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">Everyone</span>
        <span className="d2d-micro d2d-watch__note">{laneLabel}</span>
      </div>
      <div className="d2d-watchtitle__progresslines" data-testid="progress-lines">
        {shown.map((m) => (
          <MemberProgressLine
            key={m.id}
            id={m.id}
            isYou={m.id === userId}
            directory={directory}
            progress={m.progress}
            episodes={episodes}
            seasonNumber={selectedSeason ?? null}
            subscription={subscription}
            visibleRuns={visibleRuns}
          />
        ))}
      </div>
    </div>
  )
}

function WhereEveryoneIsSection({
  subscription,
  yourProgress,
  userId,
  you,
  directory,
  onChanged,
  selectedSeason,
  seasons,
}) {
  const dropped = membersWereDropped(subscription)
  const rawMembers = Array.isArray(subscription?.members) ? subscription.members : []
  const stopped = membersWithStatus(subscription, 'stopped')
  const standing = yourStanding(subscription, yourProgress, you, userId)

  return (
    <section className="d2d-watch__section" data-testid="title-where-we-are">
      <span className="d2d-micro d2d-watchtitle__sectiontitle">Where everyone is</span>

      <div className="d2d-watchtitle__standingrow">
        <div className="d2d-watchtitle__standingcard">
          <span className="d2d-micro d2d-watchtitle__standinglabel">Group · furthest ahead</span>
          <div className="d2d-watchtitle__standingvalue">{groupStandingLine(subscription)}</div>
          <div className="d2d-micro d2d-watch__note">
            {whereWeAreCaption(subscription, Date.now())
              ? `${whereWeAreCaption(subscription, Date.now())} · the group's furthest position — each member marks their own`
              : "The group's furthest position — each member marks their own."}
          </div>
          {toWatchInfo(subscription).mode === 'cross_season' ? (
            <div className="d2d-micro d2d-watch__note">NO COUNT ACROSS A SEASON BOUNDARY</div>
          ) : null}
        </div>
        {standing ? (
          <div
            className="d2d-watchtitle__standingcard d2d-watchtitle__standingcard--you"
            data-testid="your-standing"
          >
            <span className="d2d-micro d2d-watchtitle__standinglabel">You</span>
            <div className="d2d-watchtitle__standingvalue">{standing.value}</div>
            {standing.note ? (
              <div
                className={`d2d-watchtitle__standinggap${standing.tone === 'behind' ? ' d2d-watchtitle__standinggap--behind' : ''}`}
              >
                {standing.note}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {dropped ? (
        <p className="d2d-watch__flatline">
          {rawMembers.filter((m) => m.status === 'watching').length} watching,{' '}
          {rawMembers.filter((m) => m.status === 'watched').length} finished it,{' '}
          {rawMembers.filter((m) => m.status === 'stopped').length} stopped — names could not
          be shown.
        </p>
      ) : rawMembers.length === 0 ? (
        <p className="d2d-watch__flatline">Nobody has marked in yet.</p>
      ) : (
        <>
          <GroupProgressBlock
            subscription={subscription}
            seasons={seasons}
            selectedSeason={selectedSeason}
            directory={directory}
            userId={userId}
          />
          {stopped.length > 0 ? (
            <p className="d2d-micro d2d-watch__note" data-testid="who-is-in-stopped">
              STOPPED — <People directory={directory} ids={stopped} />
            </p>
          ) : null}
        </>
      )}

      <p className="d2d-watch__flatline d2d-watchtitle__note">
        Three things a member can say, all Discord commands: <code>/watch watching</code> when
        you start, <code>/watch stopped</code> if you quit without finishing, and marking the
        show’s own final episode (<code>/watch progress</code>) when you finish it. Nothing else
        on this page is a status.
      </p>

      <ActionBar
        subscription={subscription}
        yourProgress={yourProgress}
        userId={userId}
        you={you}
        onChanged={onChanged}
        progressFallback={() => <ProgressControl subscription={subscription} />}
      />
    </section>
  )
}

/**
 * The read-only "we're here" control — Handoff §5, the recommended shape,
 * drawn as copy-the-command. Every rule below is quoted from that section;
 * see the brief's §3 for why each one is load-bearing rather than decorative.
 */
function ProgressControl({ subscription }) {
  const [copied, setCopied] = useState(false)
  const command = progressCommand(subscription)

  if (command === null) {
    // 🔴 FINDING — not a state Handoff draws (its own worked example already
    // has a `last_aired`). "The episode offered is last_aired" has no
    // honest value to offer before anything has aired at all, so the
    // control does not render a command naming an episode that does not
    // exist. See docs/reports/W3b.md.
    return (
      <div className="d2d-watchtitle__control" data-testid="progress-control-empty">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">Moving it on</span>
        <p className="d2d-watch__flatline">
          Nothing has aired yet, so there is no episode to offer a command for.
        </p>
      </div>
    )
  }

  const onCopy = () => {
    try {
      // Fire-and-forget, matching the design reference exactly: the command
      // is also visible and selectable before this is ever pressed, which
      // is the honest fallback for a blocked clipboard — see the brief.
      navigator.clipboard?.writeText(command)?.catch(() => {})
    } catch {
      // Nothing to do — see above.
    }
    setCopied(true)
    window.clearTimeout(onCopy.timer)
    onCopy.timer = window.setTimeout(() => setCopied(false), 6000)
  }

  return (
    <div className="d2d-watchtitle__control" data-testid="progress-control">
      <span className="d2d-micro d2d-watchtitle__sectiontitle">Moving it on</span>
      <p className="d2d-watch__flatline">
        {
          // 🔴 P6-281 §1.4 — REWRITTEN. This used to say "the site cannot
          // change it", which has been false since the buttons shipped
          // (V2-3b): the site CAN set an episode, via the +1/catch-up
          // control above when it applies. This box exists for what that
          // control does not cover — a season boundary, catching up past
          // more than one aired episode, or correcting a mistaken mark —
          // so it now says that, honestly, rather than a blanket "cannot".
        }
        This copies the command — for an episode the buttons above don't offer.
      </p>
      <div className="d2d-watchtitle__commandbox" data-testid="progress-command">
        {command}
      </div>
      <div className="d2d-watchtitle__controlbtns">
        <button
          type="button"
          className="d2d-watchtitle__copybtn"
          data-testid="copy-command-button"
          onClick={onCopy}
        >
          {copied ? 'Copied ✓' : 'Copy the command'}
        </button>
        <a
          className="d2d-watchtitle__openbtn"
          href={DISCORD_HOME}
          target="_blank"
          rel="noreferrer"
          data-testid="open-watch-channel"
        >
          Open #watch ↗
        </a>
      </div>
      {copied ? (
        <div className="d2d-watchtitle__confirm" data-testid="copy-confirmation">
          Copied. Nothing has changed yet — paste it in <code>#watch</code> and the bot moves the
          group on.
        </div>
      ) : (
        <div className="d2d-micro d2d-watchtitle__nowrites" data-testid="nothing-writes">
          Nothing here writes to the list
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// SEASON NEWS
// ═══════════════════════════════════════════════════════════════════════════

function SeasonNewsSection({ subscription }) {
  const news = Array.isArray(subscription?.news) ? subscription.news : []
  return (
    <section className="d2d-watch__section" data-testid="season-news">
      <span className="d2d-micro d2d-watchtitle__sectiontitle">Season news</span>
      {news.length === 0 ? (
        <p className="d2d-watch__flatline">Nothing reported yet.</p>
      ) : (
        <div className="d2d-watchtitle__newslist">
          {news.map((entry, index) => {
            const ms = instantOf(entry?.at)
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${entry?.at}-${index}`} className="d2d-watchtitle__newsrow">
                <span className="d2d-micro d2d-watchtitle__newsat">
                  {ms === null ? '—' : shortEasternDate(ms)}
                </span>
                <span className="d2d-micro d2d-watchtitle__newskind">{entry?.kind}</span>
                <span className="d2d-watchtitle__newsdetail">{entry?.detail}</span>
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// EPISODE THREADS
// ═══════════════════════════════════════════════════════════════════════════

function ThreadsSection({ subscription }) {
  const threads = Object.entries(subscription?.threads ?? {}).filter(
    ([, url]) => typeof url === 'string',
  )
  return (
    <section className="d2d-watch__section" data-testid="episode-threads">
      <div className="d2d-watchtitle__sectionhead">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">Episode threads</span>
        <span className="d2d-micro d2d-watch__note">{threads.length} open</span>
      </div>
      {threads.length === 0 ? (
        <p className="d2d-micro d2d-watch__note">
          NO EPISODE THREADS — THE BOT ONLY OPENS ONE AS AN EPISODE AIRS
        </p>
      ) : (
        <>
          <div className="d2d-watchtitle__threadchips">
            {threads.map(([key, url]) => (
              <a
                key={key}
                className="d2d-watch__link"
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid="thread-chip"
              >
                {episodeCodeFromThreadKey(key) ?? key} ↗
              </a>
            ))}
          </div>
          <p className="d2d-watch__flatline d2d-watchtitle__note">
            One thread per episode that aired while the bot was following this title.
          </p>
        </>
      )}
    </section>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// V2-2b: REVIEWS — screen 2's reviews section. Large score, an avatar, the
// name (ember if it's you), the text — and the spoiler blur / didn't-finish
// box, both decided against the prototype's own source rather than guessed.
// ═══════════════════════════════════════════════════════════════════════════
//
// 🔴 THE ARCHIVE IS NOT THE SOURCE HERE. `subscription.members[]` already
// carries every member's `rating`/`review`/`watched_when`/`favorite_rank`
// (V2-2a's own `_member_facts`, merged into the SAME summary object this
// page already fetches for the hero/standing/action bar) — this section
// needs no second request and reads no field `/api/watch/archive` publishes
// that this endpoint does not already have.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 ORDERING — READ FROM THE PROTOTYPE, NOT GUESSED
// ═══════════════════════════════════════════════════════════════════════════
//
// `titleVals()`'s own `revs` array carries no `.sort()` at all — only the
// viewer's own review, when present, is `unshift`ed to the front. Every
// other review keeps `members[]`'s own order. This mirrors that exactly:
// no sort by rating or by date, because the design does neither.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE BLUR IS CSS, NEVER AN OMISSION — "a courtesy, not access control"
// ═══════════════════════════════════════════════════════════════════════════
//
// A blurred review's text is rendered into the document (`aria-hidden`, so a
// screen reader is not handed spoiler text unasked) with a `filter: blur()`
// rule in app.css — never left out of the DOM the way an unaired episode's
// title is (`SeasonBody`, above, which genuinely withholds the string until
// revealed). The two look similar and are NOT the same mechanism; see the
// report.

function ReviewsSection({ subscription, directory, userId, viewerFinished }) {
  const [revealed, setRevealed] = useState(() => new Set())
  const rawMembers = Array.isArray(subscription?.members) ? subscription.members : []
  const reviewed = rawMembers.filter(hasReviewText)
  const mine = reviewed.find((member) => joinableId(member.id) === userId)
  const others = reviewed.filter((member) => joinableId(member.id) !== userId)
  const ordered = mine ? [mine, ...others] : others

  // The brief's own wording, verbatim, matching `titleVals()`'s `revNote`:
  // one sentence when there is nothing to show yet, a different one when
  // there IS something but the viewer's own status keeps it blurred.
  const anyBlurred = !viewerFinished && others.length > 0
  const note =
    ordered.length === 0
      ? 'Reviews are written when someone finishes it.'
      : anyBlurred
        ? 'You haven’t finished it, so reviews stay blurred. Tap one to read it anyway.'
        : null

  return (
    <section className="d2d-watch__section" data-testid="title-reviews">
      <div className="d2d-watchtitle__sectionhead">
        <span className="d2d-micro d2d-watchtitle__sectiontitle">Reviews</span>
        <span className="d2d-micro d2d-watch__note">
          {ordered.length === 0 ? 'None yet' : `${ordered.length} review${ordered.length === 1 ? '' : 's'}`}
        </span>
      </div>
      {note ? <p className="d2d-watch__flatline">{note}</p> : null}
      {ordered.map((member) => {
        const id = joinableId(member.id)
        const isYou = id !== null && id === userId
        const key = id ?? String(member.id)
        return (
          <ReviewRow
            key={key}
            member={member}
            directory={directory}
            isYou={isYou}
            episodeCount={subscription?.episode_count}
            blurred={!isYou && !viewerFinished && !revealed.has(key)}
            onReveal={() => setRevealed((set) => new Set(set).add(key))}
          />
        )
      })}
    </section>
  )
}

/** One review — an avatar, the name (ember if it's you), a large score, the
 * date, the didn't-finish box (from the shared `reviewDisclaimer` helper,
 * never retyped), and the text, blurred or not. */
function ReviewRow({ member, directory, isYou, episodeCount, blurred, onReveal }) {
  const person = nameFor(directory, joinableId(member.id))
  const initial = person.known ? person.label.trim().charAt(0).toUpperCase() : '·'
  const rating = member.rating
  const hasRating = typeof rating === 'number' && Number.isFinite(rating)
  const whenMs = instantOf(member.review?.updated_at)
  // Never retyped — imported verbatim from `./reviewDisclaimer.js` (P6-282).
  // `null` for a `watching`/`watched` author or a review with no context at
  // all (written before this field existed) — no box in either case.
  const disclaimer = reviewDisclaimer(member.review?.context ?? null, episodeCount ?? null)

  return (
    <div className="d2d-watchtitle__review" data-testid="review-row">
      <div className="d2d-watchtitle__reviewhead">
        <MemberLink to={memberProfilePath(member.id)} className="d2d-watchtitle__reviewwho">
          <AvatarCircle
            person={person}
            initial={initial}
            className={`d2d-watchtitle__reviewavatar${isYou ? ' d2d-watchtitle__reviewavatar--you' : ''}`}
            imgClassName="d2d-watchtitle__reviewavatarimg"
            testId="review-avatar"
          />
          <span className={isYou ? 'd2d-watchtitle__reviewname--you' : 'd2d-watchtitle__reviewname'}>
            {person.known ? person.label : UNKNOWN_NAME}
          </span>
        </MemberLink>
        <span className="d2d-watchtitle__reviewscore">
          <span className="d2d-watchtitle__reviewnum">{hasRating ? rating : '–'}</span>
          <span className="d2d-micro d2d-watch__note">
            /10{whenMs === null ? '' : ` · ${monthYearLabel(whenMs)}`}
          </span>
        </span>
      </div>

      {disclaimer ? (
        <div className="d2d-watchtitle__dnf" role="note" data-testid="review-dnf">
          <div className="d2d-watchtitle__dnfhead">Didn’t finish</div>
          <div className="d2d-micro d2d-watchtitle__dnfline">{disclaimer}</div>
        </div>
      ) : null}

      {blurred ? (
        <div className="d2d-watchtitle__reviewblurwrap">
          <p
            aria-hidden="true"
            className="d2d-watchtitle__reviewtext d2d-watchtitle__reviewtext--blurred"
            data-testid="review-text-blurred"
          >
            {member.review.text}
          </p>
          <button
            type="button"
            className="d2d-watchtitle__revealreview"
            onClick={onReveal}
            data-testid="reveal-review"
          >
            Reveal · may spoil it
          </button>
        </div>
      ) : (
        <p className="d2d-watchtitle__reviewtext" data-testid="review-text">
          {member.review.text}
        </p>
      )}
    </div>
  )
}
